import { Button } from '@/components/ui';
import { MIGRATION_NEW_APP_CAPITAL_PLANS_URL } from '@/config/migration';
import usePrivyFunding from '@/hooks/use-privy-funding';
import useEvmBalance from '@/hooks/use-evm-balance';
import useCreateDeposit from '@/lib/api/create-deposit';
import { defaultChain, getChainDecimals } from '@/lib/wagmi/config';
import { formatBigInt } from '@/utils/value-format';
import {
  ArrowLeftRight,
  ChevronRight,
  Coins,
  CreditCard,
  Plus,
  QrCode,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import BlurFade from '@/components/ui/blur-fade';
import TooltipWrapper from '@/components/ui/tooltip';
import { useIsDepositGeoBlocked } from '@/hooks';

export default function DepositPage() {
  const { t } = useTranslation();
  const { fundWallet } = usePrivyFunding();

  const { value: balance, decimals } = useEvmBalance({
    chainId: defaultChain.id,
  });

  const { isLoading: isDepositing, createDeposit } = useCreateDeposit();

  const { isGeoBlocked } = useIsDepositGeoBlocked();

  return (
    <section className="px-6 py-8 sm:px-8 sm:py-12">
      <div className="mx-auto w-full max-w-3xl space-y-8">
        <div className="flex flex-col pb-4">
          <h1 className="mb-4 text-4xl font-bold">Deposit</h1>
          <p className="text-vestgrey-100">
            Choose how you want to start trading.
          </p>
        </div>
        <div className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">Trade our capital</h2>
            <p className="text-vestgrey-100">
              Pass an evaluation to access a funded account.
            </p>
          </div>
          <div className="space-y-3">
            <BlurFade direction="right">
              <Button
                variant="outline2"
                className="group h-auto w-full justify-between bg-muted p-5 hover:bg-muted"
                asChild
              >
                <a
                  href={MIGRATION_NEW_APP_CAPITAL_PLANS_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  <div className="flex items-center gap-4">
                    <Coins size={20} />
                    <span className="text-lg font-medium leading-none">
                      Get funded
                    </span>
                  </div>
                  <ChevronRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </a>
              </Button>
            </BlurFade>
          </div>
        </div>
        <div className="space-y-6">
          <div className="space-y-2">
            <h2 className="text-lg font-semibold">Trade your own capital</h2>
            <p className="text-vestgrey-100">
              Deposit funds to start trading instantly.
            </p>
          </div>

          <div className="text-green">
            {t('createDeposit.minDepositAmount', { amount: '1 USDC' })}
          </div>

          <div className="space-y-3">
            {balance >= 10n ** BigInt(decimals) && (
              <div className="group flex h-auto w-full items-center justify-between gap-3 border border-border bg-muted p-5 text-foreground hover:bg-muted">
                <span className="text-lg font-medium leading-none">
                  {t('fundingModal.availableBalance')}{' '}
                  <span className="font-bold">
                    {formatBigInt(balance, decimals, {
                      digits: 2,
                    })}
                  </span>
                </span>
                <Button
                  className="font-mono uppercase leading-none tracking-wider"
                  disabled={isDepositing}
                  onClick={() =>
                    createDeposit({ size: balance, chainId: defaultChain.id })
                  }
                >
                  {t('fundingModal.process')}
                </Button>
              </div>
            )}
            <BlurFade direction="right" delay={0.075}>
              <TooltipWrapper
                disabled={!isGeoBlocked}
                content={t('transferDialog.geoBlockedTooltip')}
                direction="top"
              >
                <Button
                  variant="outline2"
                  className="group h-auto w-full justify-between bg-muted p-5 hover:bg-muted"
                  disabled={isGeoBlocked}
                  onClick={() => fundWallet({ defaultFundingMethod: 'card' })}
                >
                  <div className="flex items-center gap-4">
                    <CreditCard size={20} />
                    <span className="text-lg font-medium leading-none">
                      Pay with card
                    </span>
                  </div>
                  <ChevronRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </Button>
              </TooltipWrapper>
            </BlurFade>
            <BlurFade direction="right" delay={0.1}>
              <TooltipWrapper
                disabled={!isGeoBlocked}
                content={t('transferDialog.geoBlockedTooltip')}
                direction="top"
              >
                <Button
                  variant="outline2"
                  className="group h-auto w-full justify-between bg-muted p-5 hover:bg-muted"
                  disabled={isGeoBlocked}
                  onClick={() =>
                    fundWallet({ defaultFundingMethod: 'exchange' })
                  }
                >
                  <div className="flex items-center gap-4">
                    <ArrowLeftRight size={20} />
                    <span className="text-lg font-medium leading-none">
                      Transfer from exchange
                    </span>
                  </div>
                  <ChevronRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </Button>
              </TooltipWrapper>
            </BlurFade>
            <BlurFade direction="right" delay={0.125}>
              <TooltipWrapper
                disabled={!isGeoBlocked}
                content={t('transferDialog.geoBlockedTooltip')}
                direction="top"
              >
                <Button
                  variant="outline2"
                  className="group h-auto w-full justify-between bg-muted p-5 hover:bg-muted"
                  disabled={isGeoBlocked}
                  onClick={() => fundWallet({ defaultFundingMethod: 'manual' })}
                >
                  <div className="flex items-center gap-4">
                    <QrCode size={20} />
                    <span className="text-lg font-medium leading-none">
                      Receive funds
                    </span>
                  </div>
                  <ChevronRight
                    size={16}
                    className="transition-transform group-hover:translate-x-1"
                  />
                </Button>
              </TooltipWrapper>
            </BlurFade>
            {/* <BlurFade direction="right" delay={0.15}>
              <Button
                type="button"
                variant="outline"
                className="group h-auto w-full justify-between bg-muted p-5 hover:bg-muted"
                // onClick={onOnrampInit}
                // disabled={isOnrampLoading}
                asChild
              >
                <Link to="/deposit/onramper">
                  <div className="flex items-center gap-4">
                    <Plus size={20} />
                    <span className="text-lg font-medium leading-none">
                      {t('fundingModal.seeAlternativeMethods')}
                    </span>
                  </div>
                  {false ? (
                    <Loader className="w-8" />
                  ) : (
                    <ChevronRight
                      size={16}
                      className="transition-transform group-hover:translate-x-1"
                    />
                  )}
                </Link>
              </Button>
            </BlurFade> */}
          </div>
        </div>
      </div>
    </section>
  );
}
